import React, { memo } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * IconButton - A round button with just an icon, used for navigation and actions
 * 
 * @example
 * <IconButton icon={Home} onClick={handleHome} variant="purple" />
 * <IconButton icon={Settings} size="sm" variant="gray" />
 */

export type IconButtonVariant = 'purple' | 'blue' | 'green' | 'orange' | 'red' | 'gray';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Click handler */
  onClick?: () => void;
  /** Color variant */
  variant?: IconButtonVariant;
  /** Size preset */
  size?: IconButtonSize;
  /** Disabled state */
  disabled?: boolean;
  /** Accessibility label */
  ariaLabel?: string;
  /** Tooltip text */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

const variantStyles: Record<IconButtonVariant, string> = {
  purple: 'bg-kid-purple hover:bg-purple-400 border-purple-600 text-white',
  blue: 'bg-kid-blue hover:bg-blue-400 border-blue-600 text-white',
  green: 'bg-kid-green hover:bg-green-400 border-green-600 text-white',
  orange: 'bg-kid-orange hover:bg-orange-400 border-orange-600 text-white',
  red: 'bg-kid-red hover:bg-red-400 border-red-600 text-white',
  gray: 'bg-gray-200 hover:bg-gray-300 border-gray-400 text-gray-600 dark:bg-dark-surface dark:hover:bg-dark-card dark:border-dark-border dark:text-dark-text',
};

const sizeStyles: Record<IconButtonSize, { button: string; icon: number }> = {
  sm: { button: 'w-9 h-9 sm:w-10 sm:h-10', icon: 18 },
  md: { button: 'w-11 h-11 sm:w-14 sm:h-14', icon: 22 },
  lg: { button: 'w-14 h-14 sm:w-16 sm:h-16', icon: 26 },
};

export const IconButton: React.FC<IconButtonProps> = memo(({
  icon: Icon,
  onClick,
  variant = 'purple',
  size = 'md',
  disabled = false,
  ariaLabel,
  title,
  className = '',
}) => {
  const sizeStyle = sizeStyles[size];
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={`
        ${sizeStyle.button}
        rounded-full
        flex items-center justify-center
        border-b-4
        shadow-lg hover:shadow-xl
        active:scale-95
        transition-all duration-150
        cursor-pointer
        disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
        ${variantStyles[variant]}
        ${className}
      `}
    >
      <Icon size={sizeStyle.icon} strokeWidth={2.5} />
    </button>
  );
});

IconButton.displayName = 'IconButton';
